import type { SoftphoneProps } from './index.types';
import { useSoftphoneConnectionStatus } from 'Modules/softphone/hooks';
import { useConferenceCallManager } from 'Modules/softphone/hooks/useConferenceCallManager';
import { useFocusRestoration } from 'Modules/softphone/hooks/useFocusRestoration';
import { useMultipleCallsManager } from 'Modules/softphone/hooks/useMultipleCallsManager';
import { useSoftphoneStore } from 'Modules/softphone/store';
import React, { useEffect, useRef } from 'react';
import { cn } from '~/lib/utils';
import { SoftphoneConference } from '../SoftphoneConference';
import { SoftphoneHeldCalls } from '../SoftphoneHeldCalls';
import { SoftphoneTransfer } from '../SoftphoneTransfer';
import { softphoneVariants } from './index.styles';
import { SoftphoneOnCallView } from './SoftphoneOnCallView';

/**
 * SoftphoneUI - Main call interface component
 *
 * A complete softphone UI that displays call information and controls.
 * Supports two display modes:
 * - fixed: Compact mode for sidebar (80px avatar, 48px top padding)
 * - popup: Prominent mode for draggable window (70px avatar, 28px top padding)
 */
export const SoftphoneUI = ({
  variant = 'popup',
  className,
  testID,
}: SoftphoneProps) => {
  const componentTestId = testID ? `${testID}-softphone` : 'softphone';

  useFocusRestoration();

  const { currentCall, heldCallsWithConference, hasHeldCalls, handleSwapCall } =
    useMultipleCallsManager();
  const { isConferenceActive, participants } = useConferenceCallManager();
  const { connectionQuality } = useSoftphoneConnectionStatus();

  const {
    isKeypadActive,
    isDialpadActive,
    isTransferActive,
    setDialpadActive,
    isConnectionLost,
  } = useSoftphoneStore((state) => ({
    isKeypadActive: state.isKeypadActive,
    isDialpadActive: state.isDialpadActive,
    isTransferActive: state.isTransferActive,
    setDialpadActive: state.setDialpadActive,
    isConnectionLost: state.status === 'disconnected',
  }));

  const showConferenceCollapsible =
    Boolean(isConferenceActive && currentCall?.conferenceId) &&
    !isConnectionLost;
  const isConferenceOnHold =
    showConferenceCollapsible && Boolean(currentCall?.isOnHold);

  const previousCallIdRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!currentCall) {
      previousCallIdRef.current = null;
      return;
    }
    const isNewCall = previousCallIdRef.current !== currentCall.id;
    const isCallInProgress =
      currentCall.state === 'connecting' ||
      currentCall.state === 'ringing' ||
      currentCall.state === 'active';

    if (isDialpadActive && isNewCall && isCallInProgress) {
      setDialpadActive(false);
    }

    previousCallIdRef.current = currentCall.id;
  }, [currentCall, isDialpadActive, setDialpadActive]);

  const softphoneRef = useRef<HTMLDivElement>(null);

  const transferHeldCalls =
    isTransferActive && currentCall?.isOnHold
      ? [currentCall, ...heldCallsWithConference]
      : heldCallsWithConference;

  const showHeldCalls =
    (isTransferActive ? transferHeldCalls.length > 0 : hasHeldCalls) &&
    !isConnectionLost;

  // During transfer with only the transferred call on hold, hide swap icon
  const showSwapIcon = !(isTransferActive && transferHeldCalls.length === 1);

  const showTransferScreen = isTransferActive && !isConnectionLost;

  return (
    <div
      ref={softphoneRef}
      className={cn(
        softphoneVariants({ variant, transferActive: showTransferScreen }),
        'relative',
        className
      )}
      data-testid={componentTestId}
    >
      {showHeldCalls && (
        <SoftphoneHeldCalls
          holdingCalls={transferHeldCalls}
          currentCall={currentCall}
          onSwapCall={handleSwapCall}
          showSwapIcon={showSwapIcon}
          nonSwappableCallId={isTransferActive ? currentCall?.id : null}
          boundaryRef={softphoneRef}
          testID={componentTestId}
        />
      )}

      {showConferenceCollapsible && (
        <div
          className="relative w-full z-[50]"
          aria-label="Conference participants"
          role="group"
        >
          <SoftphoneConference
            participants={participants}
            isOnHold={isConferenceOnHold}
            boundaryRef={softphoneRef}
            testID={componentTestId}
          />
        </div>
      )}

      {showTransferScreen ? (
        <SoftphoneTransfer testID={componentTestId} variant={variant} />
      ) : (
        <SoftphoneOnCallView
          variant={variant}
          testID={componentTestId}
          connectionQuality={connectionQuality}
          isDialpadActive={isDialpadActive}
          isKeypadActive={isKeypadActive}
        />
      )}
    </div>
  );
};
